import { useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { useAuth } from '../lib/auth';
import AuthLayout, { errorBox, inputClass, noticeBox, primaryButton } from './AuthLayout';
import GoogleButton, { OrDivider } from './GoogleButton';

export default function SignUp() {
  const { session } = useAuth();
  const [params] = useSearchParams();
  const next = params.get('next') || '/';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [exists, setExists] = useState(false);

  if (session) return <Navigate to={next} replace />;

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) return setError('Use at least 8 characters for your password.');
    if (password !== confirm) return setError('The passwords do not match.');
    setLoading(true);
    setExists(false);
    const { data, error } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: { emailRedirectTo: `${window.location.origin}${next}` },
    });
    setLoading(false);
    if (error) setError(error.message);
    // Supabase answers an existing email with a user without identities and sends nothing.
    else if (data.user && data.user.identities?.length === 0) setExists(true);
    else setSent(true);
  };

  const signInPath = `/login?${new URLSearchParams({ ...(next !== '/' && { next }), email: email.trim() })}`;

  return (
    <AuthLayout title="Create your account" subtitle="Your digital business card in minutes">
      {sent ? (
        <div className={noticeBox}>
          We sent a confirmation link to <strong>{email}</strong>. Open it to finish creating your account.
        </div>
      ) : (
        <>
        <GoogleButton redirectPath={next} label="Sign up with Google" />
        <OrDivider />
        <form onSubmit={handleSignUp} className="space-y-4">
          <label className="block">
            <span className="block text-sm font-medium text-ink mb-1">Email</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass}
              autoComplete="email" required disabled={loading} />
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-ink mb-1">Password</span>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputClass}
              autoComplete="new-password" required disabled={loading} />
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-ink mb-1">Confirm password</span>
            <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputClass}
              autoComplete="new-password" required disabled={loading} />
          </label>
          {error && <div className={errorBox}>{error}</div>}
          {exists && (
            <div className={errorBox} role="alert">
              There is already an account for {email.trim()}.{' '}
              <Link to={signInPath} className="font-semibold underline">Sign in</Link> instead, or choose “Forgot password?” there to set a new one.
            </div>
          )}
          <button type="submit" disabled={loading} className={primaryButton}>
            {loading ? 'Creating account...' : 'Create account'}
          </button>
        </form>
        </>
      )}
      <p className="text-center text-sm text-ink-muted mt-6">
        Already have an account?{' '}
        <Link to={`/login${next !== '/' ? `?next=${encodeURIComponent(next)}` : ''}`} className="font-medium text-ink hover:underline">
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
